import * as React from 'react'
import * as ThemeUI from 'theme-ui'
import * as PropTypes from 'prop-types'

import * as Herz from '@rushplay/herz'

import * as Icons from './icons'

export function Select(props) {
  const inputRef = React.useRef(null)
  const [focused, setFocused] = React.useState(props.autoFocus)

  // TODO: Refactor translation to be in SelectField, not Select
  const translate = Herz.I18n.useTranslate(
    () => props.options.map((option) => option.label),
    [props.options]
  )

  const value = props.value && props.value.toString()
  const valid = props.valid && !props.suppressVisualFeedback
  const invalid = !props.valid && props.visited && !props.suppressVisualFeedback

  React.useEffect(() => {
    if (focused && inputRef.current) {
      inputRef.current.focus()

      /**
       * Because of custom focus handling, re-render of component happens before the
       * onChange event. This causes the value of the field to fallback to the initial value
       * on auto-complete/fill events.
       * By calling the onChange with the value of the component before it's focused we ensure it's the correct value.
       * The side-effect though, is that onChange is fired twice.
       */

      if (props.onChange) {
        props.onChange({ target: inputRef.current.control })
      }
    }
  }, [focused, inputRef])

  function handleBlur() {
    setFocused(false)
    if (props.onBlur) {
      props.onBlur()
    }
  }

  function handleFocus() {
    setFocused(true)
    if (props.onFocus) {
      props.onFocus()
    }
  }

  return (
    <ThemeUI.Box
      as="label"
      sx={{
        '& > div': {
          'position': 'relative',
          'alignItems': 'center',
          'display': 'flex',
          'color': 'input-text',
          'backgroundColor': props.disabled
            ? 'input-disabled'
            : 'input-background',
          'border': '1px solid',
          'borderColor': borderColor({
            focused,
            valid,
            invalid,
            disabled: props.disabled,
          }),
          'borderRadius': 1,
          'fontSize': 'md',
          'flexGrow': 1,
          'flexShrink': 1,
          'overflow': 'hidden',
          'pr': 0,
          '&:hover': {
            borderColor: hover({
              valid,
              invalid,
              focused,
              disabled: props.disabled,
            }),
          },
          '&:disabled': {
            cursor: 'not-allowed',
          },
        },
      }}
      ref={inputRef}
    >
      <ThemeUI.Select
        autoFocus={props.autoFocus}
        arrow={
          <ThemeUI.Box
            sx={{
              pointerEvents: 'none',
              position: 'absolute',
              right: '0',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              color: props.disabled ? 'input-text-disabled' : 'input-text',
            }}
          >
            <Icons.ExpandMore />
          </ThemeUI.Box>
        }
        disabled={props.disabled}
        value={value}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={props.onChange}
      >
        {props.options?.map((option) => (
          <option key={option.value + option.label} value={option.value}>
            {props.contentTranslated ? option.value : translate(option.label)}
          </option>
        ))}
      </ThemeUI.Select>
    </ThemeUI.Box>
  )
}

Select.propTypes = {
  autoFocus: PropTypes.bool,
  contentTranslated: PropTypes.bool,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  suppressVisualFeedback: PropTypes.bool,
  valid: PropTypes.bool,
  visited: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
}

function borderColor(props) {
  if (props.focused) {
    return 'faded-white'
  } else if (props.disabled) {
    return 'transparent'
  } else if (props.valid) {
    return 'success'
  } else if (props.invalid) {
    return 'danger'
  } else {
    return 'input-border'
  }
}

function hover(props) {
  if (props.valid || props.invalid || props.disabled || props.focused) {
    return null
  } else {
    return 'inputHover'
  }
}
